import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import CommonModal from "./CommonModal";
import { useCommonStyles } from "../../utils";
import downloadFileFromBlob from "../../lib/downloadFileFromBlob";
import { bindFilePreview, fileName, loadFile } from "../../ApiSetUp/AuthApi";

const canPreview = (type) => type === "application/pdf" || (type || "").startsWith("image/");

const FilePreview = () => {
    const { classes } = useCommonStyles();
    const iframeRef = useRef(null);
    const urlRef = useRef(null);
    const gen = useRef(0);
    const [file, setFile] = useState(null);

    useEffect(() => {
        bindFilePreview(async (path) => {
            if (!path) return;
            const id = ++gen.current;
            if (urlRef.current) {
                URL.revokeObjectURL(urlRef.current);
                urlRef.current = null;
            }
            setFile({ loading: true, name: fileName(path) });
            const loaded = await loadFile(path);
            if (id !== gen.current) {
                if (loaded?.url) URL.revokeObjectURL(loaded.url);
                return;
            }
            urlRef.current = loaded?.url || null;
            setFile(loaded || { error: true, name: fileName(path) });
        });
        return () => {
            gen.current += 1;
            bindFilePreview(null);
        };
    }, []);

    const close = () => {
        gen.current += 1;
        const url = urlRef.current;
        urlRef.current = null;
        // ponytail: delay unmount so Close click doesn't fall through onto a file behind the dialog
        setTimeout(() => {
            setFile(null);
            if (url) setTimeout(() => URL.revokeObjectURL(url), 60000);
        }, 0);
    };

    const print = () => {
        if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.focus();
            iframeRef.current.contentWindow.print();
            return;
        }
        const w = window.open(file.url, "_blank");
        if (w) w.addEventListener("load", () => w.print());
    };

    return (
        <CommonModal
            open={Boolean(file)}
            onClose={close}
            title={file?.name || "Document"}
            isView
            modalWidth="90vw"
            maxModalWidth="1080px"
            childrenPadding="8px 0 0"
            viewActions={
                file?.url ? (
                    <>
                        <Button className={classes.cancelButton} onClick={() => window.open(file.url, "_blank")}>Open</Button>
                        <Button className={classes.cancelButton} onClick={() => downloadFileFromBlob(file.blob, file.name, file.type)}>Download</Button>
                        {canPreview(file.type) ? <Button className={classes.saveButton} onClick={print}>Print</Button> : null}
                    </>
                ) : null
            }
        >
            {file?.loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                    <CircularProgress size={32} sx={{ color: "#E77843" }} />
                </Box>
            ) : file?.error || !file?.url ? (
                <Typography fontFamily="Satoshi-Medium" color="#8A8A8A" textAlign="center" py={8}>
                    Unable to open this file.
                </Typography>
            ) : canPreview(file.type) ? (
                file.type.startsWith("image/") ? (
                    <Box textAlign="center" sx={{ maxHeight: "70vh", overflow: "auto" }}>
                        <img src={file.url} alt={file.name} style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }} />
                    </Box>
                ) : (
                    <iframe
                        ref={iframeRef}
                        title={file.name}
                        src={file.url}
                        style={{ width: "100%", height: "70vh", border: "none", borderRadius: 8, background: "#F7F7F8" }}
                    />
                )
            ) : (
                <Typography fontFamily="Satoshi-Medium" color="#8A8A8A" textAlign="center" py={8}>
                    Preview isn’t available for this file type. Use Open or Download.
                </Typography>
            )}
        </CommonModal>
    );
};

export default FilePreview;
